import { Button, Popover } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { IssueType } from '@prisma/client';
import { IconCaretDown } from '@tabler/icons-react';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { match } from 'ts-pattern';
import { queryParamToString } from '~/logic/query-param';
import type { RouterOutputs } from '~/trpc-extra/api';
import { api } from '~/trpc-extra/api';

export const issueTypeToDisplay = (itype: IssueType): string =>
  match(itype)
    .with('BUG', () => 'Bug')
    .with('NEEDS_REWRITE', () => 'Needs Update')
    .with('FLAKE', () => 'Flaky')
    .with('UI_CHANGE', () => 'UI Change')
    .with('OTHER', () => 'Unknown')
    .exhaustive();

// TODO: fix the any type for issue
export default function IssueTypeButton({ issue }: { issue?: RouterOutputs['issues']['get'] }) {
  const router = useRouter();
  const issueId = queryParamToString(router.query.issueId);
  const trpcUtils = api.useUtils();
  const [isUpdating, setIsUpdating] = useState(false);
  const [opened, setOpened] = useState(false);

  const updateIssueType = api.issues.update.useMutation({
    onMutate: () => {
      setIsUpdating(true);
      setOpened(false);
    },
    onSuccess: () => {
      trpcUtils.issues.list.invalidate().catch(() => undefined);
      trpcUtils.issues.get.invalidate({ issueId }).catch(() => undefined);
      trpcUtils.issues.getActivity.invalidate({ issueId }).catch(() => undefined);
      notifications.show({
        message: 'Issue type updated',
        color: 'green',
      });
    },
    onError: () => {
      notifications.show({
        message: 'Error updating issue type',
        color: 'red',
      });
    },
    onSettled: () => {
      setIsUpdating(false);
    },
  });

  return (
    <Popover
      shadow="md"
      width="target"
      opened={opened}
      onChange={setOpened}
    >
      <Popover.Target>
        <Button
          variant="light"
          disabled={isUpdating}
          className="w-[160px]"
          color={
            issue?.issueType === 'BUG' ? 'red'
            : issue?.issueType === 'NEEDS_REWRITE' ?
              'blue'
            : issue?.issueType === 'FLAKE' ?
              'yellow'
            : issue?.issueType === 'UI_CHANGE' ?
              'violet'
            : 'gray'
          }
          onClick={() => setOpened((o) => !o)}
        >
          <div className="flex items-center space-x-1">
            <span>{issueTypeToDisplay(issue?.issueType || 'OTHER')}</span>
            <IconCaretDown size={18} />
          </div>
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <ul>
          {(['BUG', 'FLAKE', 'NEEDS_REWRITE', 'UI_CHANGE', 'OTHER'] as const).map((itype) => (
            <li
              key={itype}
              className={`${
                itype === (issue?.issueType || 'OTHER') ?
                  'bg-gray-50 dark:bg-stone-700'
                : 'hover:bg-blue-50 dark:hover:bg-blue-900'
              } rounded-sm px-2 py-[6px] text-sm text-gray-800 hover:cursor-pointer dark:text-gray-200`}
              onClick={() => {
                if (issue && itype !== issue.issueType) {
                  updateIssueType.mutate({
                    issueIds: [issue.id],
                    projectId: issue.projectId,
                    update: { issueType: itype },
                  });
                }
              }}
            >
              {issueTypeToDisplay(itype)}
            </li>
          ))}
        </ul>
      </Popover.Dropdown>
    </Popover>
  );
}
